import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import countryService from '../services/countryService';
import iataProfileService from '../services/iataProfileService';
import OhipProfilePicker from './OhipProfilePicker';
import './AddCorporateProfilePage.css';

const PROFILE_TYPES = [
    { value: 'AGT', label: 'Agent' },
    { value: 'SRC', label: 'Source' },
];

const EMPTY_FORM = {
    profileType: 'AGT',
    corporateId: '',
    name: '',
    address: '',
    city: '',
    country: '',
};

const AddProfileIATAPage = () => {
    const navigate = useNavigate();
    const { ppId } = useParams();
    const [searchParams] = useSearchParams();
    const ruleId = searchParams.get('ruleId');
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [countries, setCountries] = useState([]);

    useEffect(() => {
        countryService.getAllCountries().then(res => {
            setCountries(res.data?.countries || []);
        }).catch(() => {});
    }, []);

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const handleProfileSelect = ({ profileType, corporateId, name }) => {
        setForm(f => ({
            ...f,
            profileType: profileType || f.profileType,
            corporateId: corporateId || f.corporateId,
            name:        name        || f.name,
        }));
        setErrors(e => ({ ...e, profileType: undefined, corporateId: undefined, name: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.profileType) e.profileType = 'Profile Type is required';
        if (!form.corporateId.trim()) e.corporateId = 'Corporate ID / IATA is required';
        if (!form.name.trim()) e.name = 'Name is required';
        return e;
    };

    const handleCreate = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            await iataProfileService.create(ppId, {
                profile_type:   form.profileType,
                corporate_id:   form.corporateId,
                name:           form.name,
                address:        form.address || null,
                city:           form.city || null,
                country:        form.country || null,
                active:         true,
            }, ruleId);
            navigate(ruleId ? `/apps/payment-porter/${ppId}/rules/${ruleId}` : `/apps/payment-porter/${ppId}`);
        } catch (err) {
            setErrors({ submit: err.response?.data?.error?.message || 'Failed to save profile' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <h1 className="acp-title">Add Profile IATA</h1>
                        </div>
                        <OhipProfilePicker
                            ppId={ppId}
                            onSelect={handleProfileSelect}
                            buttonLabel="Search OHIP Profile"
                        />
                    </div>
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">Profile Type <span className="acp-required">*</span></label>
                            <select
                                className={`acp-select${errors.profileType ? ' acp-input-error' : ''}`}
                                value={form.profileType}
                                onChange={e => set('profileType', e.target.value)}
                            >
                                {PROFILE_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                            {errors.profileType && <span className="acp-error-msg">{errors.profileType}</span>}
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Corporate ID / IATA <span className="acp-required">*</span></label>
                            <input
                                className={`acp-input${errors.corporateId ? ' acp-input-error' : ''}`}
                                type="text"
                                placeholder="Enter Corporate ID or IATA Number"
                                value={form.corporateId}
                                onChange={e => set('corporateId', e.target.value)}
                                autoFocus
                            />
                            {errors.corporateId && <span className="acp-error-msg">{errors.corporateId}</span>}
                        </div>
                    </div>

                    <div className="acp-field" style={{ maxWidth: '50%' }}>
                        <label className="acp-label">Name <span className="acp-required">*</span></label>
                        <input
                            className={`acp-input${errors.name ? ' acp-input-error' : ''}`}
                            type="text"
                            placeholder="e.g., Booking.com"
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                        />
                        {errors.name && <span className="acp-error-msg">{errors.name}</span>}
                    </div>

                    <div className="acp-field">
                        <label className="acp-label">Address</label>
                        <input
                            className="acp-input"
                            type="text"
                            placeholder="Enter address"
                            value={form.address}
                            onChange={e => set('address', e.target.value)}
                        />
                    </div>

                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">City</label>
                            <input
                                className="acp-input"
                                type="text"
                                placeholder="Enter city"
                                value={form.city}
                                onChange={e => set('city', e.target.value)}
                            />
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Country</label>
                            <select
                                className="acp-select"
                                value={form.country}
                                onChange={e => set('country', e.target.value)}
                            >
                                <option value="">Select country</option>
                                {countries.map(c => (
                                    <option key={c.code} value={c.code}>{c.description}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                </div>

                {errors.submit && <div className="acp-error-msg" style={{ margin: '0 0 1rem' }}>{errors.submit}</div>}
                <div className="acp-footer">
                    <button
                        className="acp-btn-cancel"
                        onClick={() => navigate(ruleId ? `/apps/payment-porter/${ppId}/rules/${ruleId}` : `/apps/payment-porter/${ppId}`)}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        className="acp-btn-create"
                        onClick={handleCreate}
                        disabled={saving}
                    >
                        {saving ? 'Creating...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddProfileIATAPage;
